import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Typography,
  Button,
  Space,
  Tag,
  Descriptions,
  Table,
  Modal,
  Form,
  Input,
  Select,
  message,
  Row,
  Col,
  Statistic,
  Divider,
  Spin
} from 'antd';
import {
  EditOutlined,
  ArrowLeftOutlined,
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  BankOutlined,
  FileTextOutlined
} from '@ant-design/icons';
import api from '../utils/axios';
import dayjs from 'dayjs';
import { handleApiError } from '../utils/errorHandler';
import { BreadcrumbContext } from '../components/Breadcrumb';
import { useBreadcrumb } from '../App';

const { Title, Text } = Typography;
const { Option } = Select;

interface Contact {
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  title?: string;
  department?: string;
  address?: string;
  companyId?: string;
  company?: Company;
  createdAt: string;
  updatedAt: string;
}

interface Company {
  id: string;
  name: string;
  type: string;
  address?: string;
  phone?: string;
  email?: string;
}

interface Claim {
  id: string;
  claimNumber: string;
  dateReported: string;
  status: string;
  description?: string;
  amount?: number;
  createdAt: string;
  file_number: string; // Added file_number to the interface
}

const ContactDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [contact, setContact] = useState<Contact | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form] = Form.useForm();
  const [claims, setClaims] = useState<Claim[]>([]);
  const [claimsLoading, setClaimsLoading] = useState(false);
  const { setLastItem } = useBreadcrumb();

  useEffect(() => {
    if (id) {
      fetchContact();
      fetchClaims();
    }
  }, [id]);

  useEffect(() => {
    if (contact) {
      setLastItem(`${contact.firstName} ${contact.lastName}`.trim());
    }
    return () => setLastItem(undefined);
  }, [contact, setLastItem]);

  const fetchContact = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/contacts/${id}`);
      if (response.data.success) {
        const contactData = response.data.data;
        setContact(contactData);
        form.setFieldsValue(contactData);
        
        // Update breadcrumb with contact name
        const contactName = `${contactData.firstName} ${contactData.lastName}`.trim();
        // This will be handled by the breadcrumb context in the Layout
      } else {
        message.error('Failed to fetch contact details');
      }
    } catch (error) {
      console.error('Error fetching contact:', error);
      handleApiError(error, 'Failed to fetch contact details');
    } finally {
      setLoading(false);
    }
  };

  const fetchClaims = async () => {
    try {
      setClaimsLoading(true);
      const response = await api.get('/claims', {
        params: {
          contactId: id,
          limit: 10,
          sort: 'createdAt:desc'
        }
      });
      if (response.data.success) {
        setClaims(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching claims:', error);
    } finally {
      setClaimsLoading(false);
    }
  };

  const handleEdit = () => {
    setEditing(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const response = await api.put(`/contacts/${id}`, values);
      if (response.data.success) {
        message.success('Contact updated successfully');
        setContact(response.data.data);
        setEditing(false);
      } else {
        message.error('Failed to update contact');
      }
    } catch (error) {
      console.error('Error updating contact:', error);
      handleApiError(error, 'Failed to update contact');
    }
  };

  const handleCancel = () => {
    setEditing(false);
    form.setFieldsValue(contact);
  };

  const handleBack = () => {
    navigate('/contacts');
  };

  const claimColumns = [
    {
      title: 'Claim Number',
      dataIndex: 'claimNumber',
      key: 'claimNumber',
      render: (_: string, record: Claim) => (
        <Button
          type="link"
          onClick={() => navigate(`/claims/${record.file_number}`)}
          style={{ padding: 0 }}
        >
          {record.claimNumber}
        </Button>
      ),
    },
    {
      title: 'Date Reported',
      dataIndex: 'dateReported',
      key: 'dateReported',
      render: (date: string) => dayjs(date).format('MM/DD/YYYY'),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={status === 'ACTIVE' ? 'green' : status === 'PENDING' ? 'orange' : 'red'}>
          {status}
        </Tag>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (text: string) => text || 'N/A',
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number) => amount ? `$${amount.toLocaleString()}` : 'N/A',
    },
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spin size="large" />
      </div>
    );
  }

  if (!contact) {
    return (
      <div className="p-6">
        <Card>
          <div className="text-center">
            <Title level={4}>Contact not found</Title>
            <Button onClick={handleBack} icon={<ArrowLeftOutlined />}>
              Back to Contacts
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6">
        <div className="mb-6">
          <Button 
            onClick={handleBack} 
            icon={<ArrowLeftOutlined />}
            className="mb-4"
          >
            Back to Contacts
          </Button>
          
          <div className="flex justify-between items-center">
            <Title level={2}>
              <UserOutlined className="mr-2" />
              {contact.firstName} {contact.lastName}
            </Title>
            <Space>
              {editing ? (
                <>
                  <Button type="primary" onClick={handleSave}>
                    Save
                  </Button>
                  <Button onClick={handleCancel}>
                    Cancel
                  </Button>
                </>
              ) : (
                <Button 
                  type="primary" 
                  icon={<EditOutlined />}
                  onClick={handleEdit}
                >
                  Edit Contact
                </Button>
              )}
            </Space>
          </div>
        </div>

        <Row gutter={[16, 16]}>
          <Col span={16}>
            <Card title="Contact Information">
              {editing ? (
                <Form form={form} layout="vertical">
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        name="firstName"
                        label="First Name"
                        rules={[{ required: true, message: 'Please enter first name' }]}
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="lastName"
                        label="Last Name"
                        rules={[{ required: true, message: 'Please enter last name' }]}
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        name="title"
                        label="Title"
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="department"
                        label="Department"
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        name="email"
                        label="Email"
                        rules={[
                          { type: 'email', message: 'Please enter a valid email' }
                        ]}
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="phone"
                        label="Phone"
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item
                    name="address"
                    label="Address"
                  >
                    <Input.TextArea rows={2} />
                  </Form.Item>
                </Form>
              ) : (
                <Descriptions column={2}>
                  <Descriptions.Item label="Full Name">
                    {contact.firstName} {contact.lastName}
                  </Descriptions.Item>
                  <Descriptions.Item label="Title">
                    {contact.title || 'N/A'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Department">
                    {contact.department || 'N/A'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Email">
                    {contact.email ? (
                      <a href={`mailto:${contact.email}`}>
                        <MailOutlined className="mr-1" />
                        {contact.email}
                      </a>
                    ) : (
                      'N/A'
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="Phone">
                    {contact.phone ? (
                      <a href={`tel:${contact.phone}`}>
                        <PhoneOutlined className="mr-1" />
                        {contact.phone}
                      </a>
                    ) : (
                      'N/A'
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="Address" span={2}>
                    {contact.address ? (
                      <span>
                        <EnvironmentOutlined className="mr-1" />
                        {contact.address}
                      </span>
                    ) : (
                      'N/A'
                    )}
                  </Descriptions.Item>
                </Descriptions>
              )}
            </Card>

            <Card 
              title={
                <span>
                  <FileTextOutlined className="mr-2" />
                  Related Claims ({claims.length})
                </span>
              }
              className="mt-6"
            >
              <Table
                columns={claimColumns}
                dataSource={claims}
                rowKey="id"
                loading={claimsLoading}
                pagination={false}
                locale={{
                  emptyText: 'No claims found for this contact'
                }}
              />
            </Card>
          </Col>

          <Col span={8}>
            <Card title="Company Information">
              {contact.company ? (
                <div>
                  <div className="mb-4">
                    <BankOutlined className="mr-2" />
                    <Text strong>{contact.company.name}</Text>
                    <Tag color="blue" className="ml-2">
                      {contact.company.type}
                    </Tag>
                  </div>
                  {contact.company.address && (
                    <div className="mb-2">
                      <EnvironmentOutlined className="mr-1" />
                      <Text>{contact.company.address}</Text>
                    </div>
                  )}
                  {contact.company.phone && (
                    <div className="mb-2">
                      <PhoneOutlined className="mr-1" />
                      <Text>{contact.company.phone}</Text>
                    </div>
                  )}
                  {contact.company.email && (
                    <div className="mb-2">
                      <MailOutlined className="mr-1" />
                      <Text>{contact.company.email}</Text>
                    </div>
                  )}
                  <Button 
                    type="link" 
                    onClick={() => navigate(`/companies/${contact.company.id}`)}
                    style={{ padding: 0 }}
                  >
                    View Company Details
                  </Button>
                </div>
              ) : (
                <div className="text-center">
                  <Tag color="green">Individual Contact</Tag>
                  <Text type="secondary" className="block mt-2">
                    This contact is not associated with any company
                  </Text>
                </div>
              )}
            </Card>

            <Card title="Contact Statistics" className="mt-6">
              <Statistic
                title="Total Claims"
                value={claims.length}
                valueStyle={{ color: '#3f8600' }}
              />
              <Divider />
              <div className="text-sm text-gray-500">
                <div>Created: {dayjs(contact.createdAt).format('MM/DD/YYYY')}</div>
                <div>Last Updated: {dayjs(contact.updatedAt).format('MM/DD/YYYY')}</div>
              </div>
            </Card>
          </Col>
        </Row>
      </div>
  );
};

export default ContactDetails; 